// ──────────────────────────────────────────────────────────────────────
// Iter 63 Item 2 — UserChip
//
// Single source of truth for rendering a user's display name with their
// role suffix in parentheses. Use everywhere a user's name appears as a
// chip, list item, pill, or text — instead of sprinkling string concat
// across the codebase.
//
// Usage:
//   <UserChip user={u} showRole />                  // "Rishav Patel (Manager)"
//   <UserChip user={u} showRole avatar />           // [RP] Rishav Patel (Manager)
//   <UserChip user={u} />                           // "Rishav Patel"
//
// Roles: Owner / Admin / Manager / Member (mapped from raw backend
// values: super_admin → Owner, owner → Owner, etc.)
// For users with multi-workspace roles, callers should pass the role
// for the CURRENT workspace as user.role.
// ──────────────────────────────────────────────────────────────────────
import { roleLabel } from './AssigneeMultiSelect';

function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserChip({
  user,
  showRole = false,
  avatar = false,
  size = 'sm',           // 'xs' | 'sm' | 'md'
  className = '',
  testId,
}) {
  if (!user) return null;
  const name = user.name || user.email || '?';
  const role = showRole ? roleLabel(user.role) : null;
  const text = role ? `${name} (${role})` : name;
  const avatarSize = size === 'xs' ? 'h-4 w-4 text-[8px]'
                    : size === 'md' ? 'h-7 w-7 text-[11px]'
                    : 'h-5 w-5 text-[9px]';
  const textSize = size === 'xs' ? 'text-[10px]'
                  : size === 'md' ? 'text-[13px]'
                  : 'text-[12px]';
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid={testId || `user-chip-${user.id}`}>
      {avatar && (
        <span className={`${avatarSize} rounded-full bg-secondary flex items-center justify-center font-medium text-foreground/70 shrink-0`}>
          {initials(name)}
        </span>
      )}
      <span className={`${textSize} truncate`}>
        {name}
        {role && <span className="text-muted-foreground/45 ml-1">({role})</span>}
      </span>
    </span>
  );
}

export { roleLabel };
